/**
 * Extranet top bar (design `layout.barre`): mobile menu button, « Espace
 * client · {client} », the session switcher of SUPERVUE users, the
 * notifications bell (pending pieces → `/pieces-attendues`) and the user menu
 * (« Se déconnecter »).
 *
 * Pure chrome — not a registered configurable component. Every string lives
 * in {@link HEADER_TEXT}.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bell, LogOut, Menu } from 'lucide-react';

import { Button, DropdownMenu } from '@components/base/actions';
import { Avatar, Badge, Icon, Text } from '@components/base/display';
import { HStack } from '@components/base/layout';
import { SessionSwitcher } from '@/shared/session/SessionSwitcher';

import { useExtranetAuthStore } from '../auth/store';
import { usePendingPiecesCount } from '../hooks/usePendingPiecesCount';
import { extranetPath } from '../navigation';
import { formatPersonName } from '../utils/format';
import { HEADER_TEXT } from './chromeText';

/** Sub-path of the pending pieces screen the bell opens. */
const PIECES_PATH = 'pieces-attendues';

export interface ExtranetHeaderProps {
  /** Opens the navigation drawer (shown below `lg` only). */
  onOpenMenu?: () => void;
}

export function ExtranetHeader({ onOpenMenu }: Readonly<ExtranetHeaderProps>) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();

  const user = useExtranetAuthStore((s) => s.user);
  const person = useExtranetAuthStore((s) => s.person);
  const sessionPerson = useExtranetAuthStore((s) => s.sessionPerson);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);
  const canSwitchSession = useExtranetAuthStore((s) => s.canSwitchSession);
  const availableSessions = useExtranetAuthStore((s) => s.availableSessions);
  const switchSession = useExtranetAuthStore((s) => s.switchSession);
  const logout = useExtranetAuthStore((s) => s.logout);

  const { count: pendingPieces } = usePendingPiecesCount();

  // The client space is the one of the active session (a SUPERVUE user may
  // work on another person's session); NOM_PER of that session.
  const clientName = sessionPerson?.nom?.trim() ?? person?.nom?.trim() ?? '';
  const userName =
    (person ? formatPersonName(person.prenom, person.nom) : '') || (user?.name ?? '');

  const sessionOptions = useMemo(
    () =>
      availableSessions.map((s) => ({
        id: s.session,
        label: s.lib ?? String(s.session),
      })),
    [availableSessions],
  );

  const handleSessionChange = useCallback(
    (id: number, label: string) => {
      void switchSession(id, label);
    },
    [switchSession],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    void navigate(extranetPath(tenantSlug), { replace: true });
  }, [logout, navigate, tenantSlug]);

  return (
    <header
      className="sticky top-0 z-20 flex h-16 w-full items-center justify-between gap-3 border-b border-secondary-200 bg-white px-4 lg:px-8"
      data-testid="extranet-header"
    >
      <HStack gap={2} align="center" className="min-w-0">
        {onOpenMenu && (
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            leftIcon={<Icon icon={Menu} size="md" />}
            aria-label={HEADER_TEXT.openMenu}
            onClick={onOpenMenu}
          />
        )}
        {clientName !== '' && (
          <Text as="span" size="xs" color="muted" className="hidden sm:block" truncate>
            {HEADER_TEXT.clientPrefix}{' '}
            <Text as="span" size="xs" weight="medium" color="default" data-testid="extranet-client-name">
              {clientName}
            </Text>
          </Text>
        )}
      </HStack>

      <HStack gap={2} align="center">
        {canSwitchSession && (
          <SessionSwitcher
            sessions={sessionOptions}
            activeSessionId={sessionId}
            onSessionChange={handleSessionChange}
            searchable
          />
        )}

        <span className="relative inline-flex">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Icon icon={Bell} size="sm" />}
            aria-label={HEADER_TEXT.notificationsAria(pendingPieces)}
            onClick={() => {
              void navigate(extranetPath(tenantSlug, PIECES_PATH));
            }}
          >
            <span className="hidden sm:inline">{HEADER_TEXT.notifications}</span>
          </Button>
          {pendingPieces > 0 && (
            <Badge
              variant="warning"
              size="sm"
              pill
              className="pointer-events-none absolute -right-1 -top-1"
              data-testid="extranet-pending-pieces-badge"
            >
              {pendingPieces}
            </Badge>
          )}
        </span>

        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button variant="ghost" size="sm" aria-label={HEADER_TEXT.userMenu}>
              <HStack gap={2} align="center">
                <Avatar name={userName} size="xs" />
                <span className="hidden sm:inline">{userName}</span>
              </HStack>
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            <DropdownMenu.Item
              icon={<Icon icon={LogOut} size="sm" />}
              destructive
              onSelect={() => {
                void handleLogout();
              }}
            >
              {HEADER_TEXT.logout}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </HStack>
    </header>
  );
}
